import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import FormTextField from "../atoms/FormTextField";
import { COLORS } from "../../styles/uiConstants";
import {
  dialogPaperSx,
  dialogTitleSx,
  dialogContentSx,
  dialogMessageSx,
  dialogActionsSx,
  dialogCancelButtonSx,
  dialogConfirmButtonSx,
} from "../common/dialogStyles";

const CONFIRM_COLORS = {
  error: { bg: COLORS.error, hover: COLORS.errorDark },
  warning: { bg: COLORS.warningDark, hover: COLORS.warningDarker },
  primary: { bg: COLORS.successDark, hover: COLORS.successDarker },
};

/**
 * Metin (gerekçe / not) isteyen onay diyaloğu. Görünüm `ConfirmDialog` ile
 * aynıdır (slate50 başlık şeridi + mesaj + İptal / Onayla); mesajın altına
 * bir metin alanı ekler. Muayene iptali, kullanıcı reddi, randevu iptali gibi
 * "sebep yazın" akışları için.
 *
 * Dialog her açıldığında değer `initialValue`'ya sıfırlanır.
 *
 * @param {boolean} open
 * @param {() => void} onClose - İptal veya dışarı tıklanınca (loading iken çağrılmaz)
 * @param {(value: string) => void} onConfirm - Onayla; kırpılmış (trim) metinle çağrılır
 * @param {string} [title="Onay"]
 * @param {string|React.ReactNode} [message]
 * @param {string} [inputLabel]
 * @param {string} [placeholder]
 * @param {boolean} [required=false] - Boş / sadece boşluk iken Onayla pasif
 * @param {boolean} [multiline=true]
 * @param {number} [minRows=3]
 * @param {string} [confirmText="Onayla"]
 * @param {string} [cancelText="İptal"]
 * @param {"error"|"warning"|"primary"} [confirmVariant="error"]
 * @param {boolean} [loading=false]
 * @param {string} [loadingText="İşleniyor..."]
 * @param {string} [initialValue=""]
 * @param {number} [maxLength] - Metin alanı karakter sınırı
 */
export default function PromptDialog({
  open,
  onClose,
  onConfirm,
  title = "Onay",
  message,
  inputLabel,
  placeholder,
  required = false,
  multiline = true,
  minRows = 3,
  confirmText = "Onayla",
  cancelText = "İptal",
  confirmVariant = "error",
  loading = false,
  loadingText = "İşleniyor...",
  initialValue = "",
  maxLength,
}) {
  const [value, setValue] = useState(initialValue);

  // Her açılışta sıfırla — önceki gerekçe bir sonraki kayda taşınmasın.
  useEffect(() => {
    if (open) setValue(initialValue ?? "");
  }, [open, initialValue]);

  const trimmed = value.trim();
  const confirmDisabled = loading || (required && !trimmed);
  const confirmColor = CONFIRM_COLORS[confirmVariant] || CONFIRM_COLORS.error;

  const handleConfirm = () => {
    if (confirmDisabled) return;
    onConfirm?.(trimmed);
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: dialogPaperSx }}
    >
      <DialogTitle sx={dialogTitleSx}>
        {title}
      </DialogTitle>
      <DialogContent sx={dialogContentSx}>
        <Box sx={{ paddingTop: "4px", display: "flex", flexDirection: "column", gap: 2 }}>
          {message ? (
            typeof message === "string" ? (
              <Typography sx={dialogMessageSx}>
                {message}
              </Typography>
            ) : (
              <Box>{message}</Box>
            )
          ) : null}
          <FormTextField
            fullWidth
            autoFocus
            label={inputLabel}
            placeholder={placeholder}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            required={required}
            multiline={multiline}
            minRows={multiline ? minRows : undefined}
            disabled={loading}
            inputProps={maxLength ? { maxLength } : undefined}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={dialogActionsSx}>
        <Button
          onClick={onClose}
          disabled={loading}
          variant="outlined"
          sx={dialogCancelButtonSx}
        >
          {cancelText}
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={confirmDisabled}
          variant="contained"
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
          sx={dialogConfirmButtonSx(confirmColor)}
        >
          {loading ? loadingText : confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
